import { AzureVectorStoreSettings } from "@/types/VectorStoreSettings";
import AzureVectorStoreSettingsForm from "./AzureVectorStoreSettingsForm";
import { forwardRef, useState, useImperativeHandle, useRef, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MilvusVectorStoreSettingsForm from "./MilvusVectorStoreSettingsForm";

type Props = {
    data?: AzureVectorStoreSettings;
    initialData?: any;
    onDataChange?: (data: any) => void;
    readOnly?: boolean;
    error?: string;
    submitButton?: React.ReactNode;
    onSubmit?: (data: any) => void;
    hideSubmitButton?: boolean;
};

const VECTOR_STORES: { label: string; value: "azure" | "milvus"; image: string }[] = [
    {
        label: "Azure",
        value: "azure",
        image: "/assets/providers/azure.svg",
    },
    {
        label: "Milvus",
        value: "milvus",
        image: "/assets/providers/milvus.svg",
    },
];

const VectorStoreSettingsForm = forwardRef(
    ({ data, initialData = {}, onDataChange, onSubmit, ...props }: Props, ref) => {
        const [vectorStoreProvider, setVectorStoreProvider] = useState<"azure" | "milvus" | string>(
            initialData?.VECTOR_STORE_PROVIDER || data?.VECTOR_STORE_PROVIDER || "azure"
        );

        const formRef: any = useRef(null);

        // Expose methods to parent component
        useImperativeHandle(ref, () => ({
            validateForm: async () => {
                if (formRef.current && formRef.current.validateForm) {
                    return await formRef.current.validateForm();
                }
                return true;
            },
            getFormData: () => {
                const displayNameInput = document.getElementById("display-name-vector-store") as HTMLInputElement;
                const providerData = {
                    VECTOR_STORE_PROVIDER: vectorStoreProvider,
                    display_name: displayNameInput?.value || "",
                };
                if (formRef.current && formRef.current.getFormData) {
                    return {
                        ...providerData,
                        ...formRef.current.getFormData(),
                    };
                }
                return providerData;
            },
            submitForm: () => {
                if (formRef.current && formRef.current.submitForm && onSubmit) {
                    formRef.current.submitForm();
                }
            },
        }));

        // Update parent component when vectorStore provider changes
        useEffect(() => {
            if (onDataChange) {
                onDataChange({
                    ...initialData,
                    VECTOR_STORE_PROVIDER: vectorStoreProvider,
                });
            }
        }, [vectorStoreProvider]); // Remove initialData and onDataChange from dependencies

        const handleDataChange = (formData: any) => {
            if (onDataChange) {
                const displayNameInput = document.getElementById("display-name-vector-store") as HTMLInputElement;
                onDataChange({
                    ...formData,
                    VECTOR_STORE_PROVIDER: vectorStoreProvider,
                    display_name: displayNameInput?.value || "",
                });
            }
        };

        const handleSubmit = (_data: any) => {
            if (!onSubmit) return;

            const displayNameInput = document.getElementById("display-name-vector-store") as HTMLInputElement;
            const __data = {
                ...data,
                ..._data,
                VECTOR_STORE_PROVIDER: vectorStoreProvider,
                display_name: displayNameInput?.value || "",
            };

            onSubmit(__data);
        };

        const renderForm = (vectorStoreProvider: "azure" | "milvus" | string) => {
            const formProps = {
                ...props,
                onSubmit: handleSubmit,
                data: {
                    ...data,
                    ...initialData,
                },
                onDataChange: handleDataChange,
            };

            switch (vectorStoreProvider) {
                case "azure":
                    return <AzureVectorStoreSettingsForm {...formProps} ref={formRef} />;
                case "milvus":
                    return <MilvusVectorStoreSettingsForm {...formProps} ref={formRef} />;
                default:
                    return <MilvusVectorStoreSettingsForm {...formProps} ref={formRef} />;
            }
        };

        return (
            <div className="space-y-4">
                <div className="space-y-2">
                    <Label htmlFor="display-name-vector-store">Display Name</Label>
                    <Input
                        id="display-name-vector-store"
                        type="text"
                        defaultValue={initialData?.display_name || ""}
                        onChange={(e) => {
                            if (onDataChange) {
                                onDataChange({
                                    ...initialData,
                                    display_name: e.target.value,
                                    VECTOR_STORE_PROVIDER: vectorStoreProvider,
                                });
                            }
                        }}
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="vector-store-provider">Provider</Label>
                    <Select defaultValue={vectorStoreProvider} onValueChange={setVectorStoreProvider}>
                        <SelectTrigger className="h-10">
                            <SelectValue placeholder="Select VectorStore" id="vector-store-provider" />
                        </SelectTrigger>
                        <SelectContent>
                            {VECTOR_STORES.map((item) => (
                                <SelectItem key={item.value} value={item.value}>
                                    <div className="flex items-center gap-2">
                                        <img src={item.image} alt={item.label} width="20" height="20" />
                                        {item.label}
                                    </div>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                {renderForm(vectorStoreProvider)}
            </div>
        );
    }
);

VectorStoreSettingsForm.displayName = "VectorStoreSettingsForm";

export default VectorStoreSettingsForm;
